"use client";

import { useEffect, useRef } from "react";
import {
  Channel,
  type Coverage,
  type OfferViewModel,
  type ProductType,
  type PublicCustomerContext,
  type Quote,
  type WebShopPublicUser,
} from "@/api_client";
import { useCustomerContext } from "@/hooks/api/customer_context/useCustomerContext";
import { useOfferById } from "@/hooks/api/offer/useOfferById";
import { useCalculateProduct } from "@/hooks/api/product/useCalculateProduct";
import { useSetCoverageSelected } from "@/hooks/api/product/useSetCoverageSelected";
import { useSetDeductibleAmount } from "@/hooks/api/product/useSetDeductibleAmount";
import { useSetInsuranceAmount } from "@/hooks/api/product/useSetInsuranceAmount";
import { useUser } from "@/hooks/api/useUser";
import useCoveragesByCorname from "@/hooks/useCoveragesByCorname";
import { useLanguageFromUrl } from "@/hooks/useLanguageFromUrl";
import useQuoteByIdQueryParam from "@/hooks/useQuoteByIdQueryParam";
import { useProductTypeFromPath } from "@/utils/useProductTypeFromPath";
import { dataLayerPusher } from "../dataLayerPusher/dataLayerPusher";
import { useGetMatchedPageType } from "./matchedPageType";

// Module-level guard map persisting across component remounts within the same SPA session.
const checkoutJourneyGuard: Record<
  string,
  { page?: boolean; checkout?: boolean }
> = {};

// Consolidated context object passed to individual page handlers to keep signatures lean.
interface EmitContext {
  contextId: string;
  customerContext?: PublicCustomerContext;
  language?: string;
  channel: Channel;
  existingCustomer?: boolean;
  coveragesByCoreName?: Record<string, Coverage>;
  productType?: ProductType;
  product?: Quote;
  enigmatizedProduct?: Quote;
  offer?: OfferViewModel;
  user?: WebShopPublicUser;
}

type PageHandler = (ctx: EmitContext) => boolean; // returns true if an event was emitted

export const handleEntryProductPage: PageHandler = ({
  productType,
  channel,
  customerContext,
}) => {
  if (!productType) return false;
  dataLayerPusher.productPageImpression(
    customerContext?.publicId ?? "",
    productType,
    channel,
  );
  return true;
};

const handleTopQuestionsPage: PageHandler = ({
  productType,
  offer,
  product,
  enigmatizedProduct,
  channel,
  language,
  user,
}) => {
  if (!productType || !offer) return false;
  dataLayerPusher.pageLoaded({
    pageType: "Webshop Page",
    pageLoadEventType: "Page Load",
    productType,
    product,
    enigmatizedProduct,
    enigmatizedPolicyHolder: offer.enigmatized?.policyHolder,
    processStepName: "webshopentry",
    language: language ?? "fi",
    user,
    channel,
  });
  return true;
};

const handlePricePage: PageHandler = ({
  contextId,
  productType,
  offer,
  product,
  enigmatizedProduct,
  channel,
  language,
  user,
  coveragesByCoreName,
}) => {
  if (
    !productType ||
    !offer ||
    !product ||
    !offer?.enigmatized?.policyHolder ||
    !user
  )
    return false;
  dataLayerPusher.pageLoaded({
    pageType: "Webshop Page",
    pageLoadEventType: "Virtual Page Load",
    productType,
    product,
    enigmatizedProduct,
    enigmatizedPolicyHolder: offer.enigmatized?.policyHolder,
    processStepName: "price",
    language: language ?? "fi",
    user,
    channel,
    includedKeys: ["customerInfo", "environmentInfo", "pageInfo"],
  });
  dataLayerPusher.priceCalculated(
    contextId,
    product,
    enigmatizedProduct,
    channel,
    offer.enigmatized?.policyHolder,
    user,
    coveragesByCoreName,
  );
  return true;
};

const handleCartPage: PageHandler = ({
  offer,
  channel,
  language,
  user,
  contextId,
  coveragesByCoreName,
}) => {
  if (!offer) return false;
  dataLayerPusher.pageLoadedMultiProduct({
    pageType: "Webshop Page",
    pageLoadEventType: "Page Load",
    enigmatizedPolicyHolder: offer.enigmatized?.policyHolder,
    processStepName: "shopping cart",
    language: language ?? "fi",
    user,
    channel,
    contextId,
    products: offer.quotes || [],
    enigmatizedProducts: offer.enigmatized?.quotes || {},
    coverageContent: coveragesByCoreName || {},
  });
  return true;
};

const handleCheckoutPage: PageHandler = ({
  offer,
  channel,
  language,
  customerContext,
  user,
  coveragesByCoreName,
  contextId,
}) => {
  if (!offer) return false;
  if (!checkoutJourneyGuard[contextId]) {
    checkoutJourneyGuard[contextId] = {};
  }
  const guard = checkoutJourneyGuard[contextId];
  if (!guard.page) {
    dataLayerPusher.pageLoadedMultiProduct({
      pageType: "Webshop Page",
      pageLoadEventType: "Virtual Page Load",
      enigmatizedPolicyHolder: offer.enigmatized?.policyHolder,
      language: language ?? "fi",
      user,
      channel,
      contextId,
      products: offer.quotes ?? [],
      enigmatizedProducts: offer.enigmatized?.quotes ?? {},
      coverageContent: coveragesByCoreName ?? {},
      includedKeys: ["customerInfo", "environmentInfo", "pageInfo"],
    });
    guard.page = true;
  }
  if (!guard.checkout) {
    dataLayerPusher.checkoutInitiated(
      customerContext?.publicId ?? "",
      offer,
      channel,
      coveragesByCoreName,
    );
    guard.checkout = true;
  }
  return true; // Always true if offer exists; events may be partially suppressed by guards
};

const handleConfirmationPage: PageHandler = ({
  offer,
  channel,
  customerContext,
  language,
  user,
  contextId,
  coveragesByCoreName,
}) => {
  if (!offer?.offer?.purchaseSuccess) return false;
  dataLayerPusher.pageLoadedMultiProduct({
    pageType: "Webshop Confirmation Page",
    pageLoadEventType: "Page Load",
    enigmatizedPolicyHolder: offer.enigmatized?.policyHolder,
    language: language ?? "fi",
    user,
    channel,
    contextId,
    products: offer.quotes ?? [],
    enigmatizedProducts: offer.enigmatized?.quotes ?? {},
    coverageContent: coveragesByCoreName ?? {},
    includedKeys: ["customerInfo", "environmentInfo", "pageInfo"],
  });
  // Use the correct publicId that was active during the purchase journey.
  const publicIdForOffer = customerContext?.contextIdByOfferId
    ? offer.offer.id && customerContext.contextIdByOfferId[offer.offer.id]
    : undefined;
  dataLayerPusher.purchaseCompleted(
    publicIdForOffer ?? customerContext?.publicId ?? "",
    offer,
    channel,
    coveragesByCoreName,
  );
  return true;
};

// Map of page impression types to their specific handlers. Keeping this object small
// keeps the hook itself simple and reduces its cognitive complexity.
const pageHandlers: Record<string, PageHandler> = {
  EntryProductPage: handleEntryProductPage,
  TopQuestionsPage: handleTopQuestionsPage,
  PricePage: handlePricePage,
  CartPage: handleCartPage,
  CheckoutPage: handleCheckoutPage,
  ConfirmationPage: handleConfirmationPage,
};

export function usePageLoadedEmitters() {
  const pageImpressionType = useGetMatchedPageType();
  const productType = useProductTypeFromPath();
  const firedKeysRef = useRef<Set<string>>(new Set());
  const { data: customerContext, isLoading: isLoadingCustomerContext } =
    useCustomerContext();
  const language = useLanguageFromUrl();
  const { data: offer, isLoading: isLoadingOffer } = useOfferById();
  const { data: user, isLoading: isLoadingUser } = useUser();
  const { isPending: isCalculating } = useCalculateProduct();
  const { isPending: coverageSelectedPending } = useSetCoverageSelected();
  const { isPending: deductibleAmountSetPending } = useSetDeductibleAmount();
  const { isPending: insuranceAmountSetPending } = useSetInsuranceAmount();
  const coveragesByCoreName = useCoveragesByCorname();
  const { quote, enigmatizedQuote, existingCustomer } =
    useQuoteByIdQueryParam();

  const isLoading =
    isCalculating ||
    coverageSelectedPending ||
    insuranceAmountSetPending ||
    deductibleAmountSetPending ||
    isLoadingOffer ||
    isLoadingUser ||
    isLoadingCustomerContext;

  useEffect(() => {
    if (isLoading || !pageImpressionType) return;

    const key = `${pageImpressionType}:${customerContext?.publicId ?? ""}`;
    if (firedKeysRef.current.has(key)) return;

    const ctx: EmitContext = {
      contextId: customerContext?.publicId ?? "",
      customerContext: customerContext ?? {},
      language,
      channel: customerContext?.channel ?? Channel.WEB_SHOP,
      existingCustomer,
      coveragesByCoreName,
      productType,
      product: quote,
      enigmatizedProduct: enigmatizedQuote,
      offer,
      user,
    };

    const handler = pageHandlers[pageImpressionType];
    const emitted = handler ? handler(ctx) : false;
    if (!emitted) return; // Do not mark as fired if prerequisites were missing

    firedKeysRef.current.add(key);

    // Leaving checkout page: clear its guard so a future return is a fresh journey.
    if (pageImpressionType !== "CheckoutPage") {
      // Reset guard entry instead of deleting to satisfy lint rules around dynamic deletes.
      checkoutJourneyGuard[customerContext?.publicId ?? ""] = {};
    }
  }, [
    isLoading,
    pageImpressionType,
    productType,
    quote,
    enigmatizedQuote,
    offer,
    user,
    existingCustomer,
    customerContext,
    language,
    coveragesByCoreName,
  ]);
}
